import * as React from 'react';
import { Box, Chip, IconButton, styled } from '@mui/material';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { JourneyPageScenario } from 'components/order/model/Order';
import { Download } from '@mui/icons-material';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { SettingDisplayText } from '../SettingDisplayText';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { Option } from 'lib/types';

const PREFIX = 'OrderingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    menuAwardsIcon: `${PREFIX}-menuAwardsIcon`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.menuAwardsIcon}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '200px',
        maxWidth: '100%'
    }
}));

const orderingBusynessOptions: Option[] = [
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Busy', value: 'BUSY' },
    { label: 'Quiet', value: 'QUIET' }
];

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    locationId: string;
    handleSubmitOrdering: (values: Record<string, any>) => Promise<boolean>;
};

export const OrderingCard = (props: IProps) => {
    const { handleSubmitOrdering, settings, tenantId, locationId } = props;

    const { loading: menuAwardsIconLoading, handleDownload: handleDownloadMenuAwardsIcon } = useDownloadAsset(
        {
            src: `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/menu_awards_cta_icon`,
            fileName: 'menu_awards_cta_icon'
        }
    );

    const orderingBusynessLabel = React.useMemo(
        () =>
            settings
                ? orderingBusynessOptions.find(option => option.value === settings.busyness)?.label ?? 'None'
                : 'None',
        [settings]
    );

    return (
        <StyledMuiCardForm
            title="Ordering"
            initialValues={{
                menuCardImage: `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_menu_selection`,
                menuAwardsIcon: `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/menu_awards_cta_icon`,
                showRecentOrders: settings?.app?.showRecentOrders ?? true,
                showCategoryGroups: !!settings?.app?.showCategoryGroups,
                cardCheckEnabledTable: settings?.orderCardCheck?.TABLE ?? true,
                cardCheckEnabledTab: settings?.orderCardCheck?.TAB ?? true,
                cardCheckEnabledOrderToTable: !!settings?.orderCardCheck?.ORDER_TO_TABLE,
                cardCheckEnabledPreorder: !!settings?.orderCardCheck?.PREORDER,
                cardCheckEnabledPayAtPos: !!settings?.orderCardCheck?.PAY_AT_POS,
                calorieNutrientName: settings?.menu?.calorieNutrientName,
                buyNowEnabled: settings?.menu?.buyNowEnabled ?? false,
                useProductDetailsImageInMenuListing:
                    settings?.menu?.useProductDetailsImageInMenuListing ?? false,
                allergenUrl: settings?.menu?.allergenUrl,
                preorderEnabledJourney:
                    settings?.web?.journeyPageScenarios?.includes(JourneyPageScenario.PREORDER) ?? false,
                ottEnabledJourney:
                    settings?.web?.journeyPageScenarios?.includes(JourneyPageScenario.ORDER_TO_TABLE) ?? true,
                dineInEnabledJourney:
                    settings?.web?.journeyPageScenarios?.includes(JourneyPageScenario.DINE_IN) ?? false,
                patEnabledJourney:
                    settings?.web?.journeyPageScenarios?.includes(JourneyPageScenario.PAY_AT_TABLE) ?? true,
                orderingBusyness: settings?.busyness,
                orderingEnabled: settings?.orderingEnabled ?? false
            }}
            onSubmit={handleSubmitOrdering}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    {locationId === undefined && (
                        <>
                            <SettingDisplaySwitch
                                title="Show Recent Orders"
                                checked={settings?.app?.showRecentOrders ?? true}
                            />
                            <SettingDisplaySwitch
                                title="Show Parent Categories"
                                checked={!!settings?.app?.showCategoryGroups}
                            />
                            <SettingDisplaySwitch
                                title="Card check enabled - Table"
                                description="Check the first user to join a table order has a stored card"
                                checked={settings?.orderCardCheck?.TABLE ?? true}
                            />
                            <SettingDisplaySwitch
                                title="Card check enabled - Tab"
                                description="Check the first user to join a tab order has a stored card"
                                checked={settings?.orderCardCheck?.TAB ?? true}
                            />
                            <SettingDisplaySwitch
                                title="Card check enabled - Order to Table"
                                description="Check the first user to join an order to table order has a stored card"
                                checked={!!settings?.orderCardCheck?.ORDER_TO_TABLE}
                            />
                            <SettingDisplaySwitch
                                title="Card check enabled - Preorder"
                                description="Check the first user to join a preorder has a stored card"
                                checked={!!settings?.orderCardCheck?.PREORDER}
                            />
                            <SettingDisplaySwitch
                                title="Card check enabled - Pay at POS"
                                description="Check the first user to join a pay at pos order has a stored card"
                                checked={!!settings?.orderCardCheck?.PAY_AT_POS}
                            />
                            <SettingDisplayText
                                title="Calorie Nutrient Name"
                                text={settings?.menu?.calorieNutrientName}
                            />
                            <SettingDisplaySwitch
                                title="Buy Now Button on All Products"
                                checked={settings?.menu?.buyNowEnabled ?? false}
                            />
                            <SettingDisplaySwitch
                                title="Use Product Details Image in Menu Listing"
                                checked={settings?.menu?.useProductDetailsImageInMenuListing ?? true}
                            />
                        </>
                    )}
                    {locationId !== undefined && (
                        <SettingDisplaySwitch
                            title="Show Parent Categories"
                            checked={!!settings?.app?.showCategoryGroups}
                        />
                    )}
                    <SettingDisplayText title="Allergen Link" text={settings?.menu?.allergenUrl} />
                    {locationId !== undefined && (
                        <>
                            <SettingDisplaySwitch
                                checked={settings?.orderingEnabled}
                                title="Ordering Enabled"
                                description="This setting controls Preorder, Dine In, Order to Table, Pay at Table and Delivery ordering"
                            />
                            <SettingDisplay label="Busyness">
                                <Chip color="primary" label={orderingBusynessLabel} />
                            </SettingDisplay>
                        </>
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplay
                                label="Menu Awards CTA Icon"
                                description="200px by 200px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img
                                        src={`${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/menu_awards_cta_icon`}
                                        className={classes.menuAwardsIcon}
                                    />
                                    <IconButton
                                        disabled={menuAwardsIconLoading}
                                        onClick={handleDownloadMenuAwardsIcon}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                            <SettingDisplaySwitch
                                title="Preorder module shown on Web Journey Page"
                                description="If Preorder is not supported in location services, the module will not appear even when enabled"
                                checked={
                                    settings?.web?.journeyPageScenarios?.includes(
                                        JourneyPageScenario.PREORDER
                                    ) ?? false
                                }
                            />
                            <SettingDisplaySwitch
                                title="Order to Table module shown on Web Journey Page"
                                description="If Order to Table is not supported in location services,
                                    the module will not appear even when enabled"
                                checked={
                                    settings?.web?.journeyPageScenarios?.includes(
                                        JourneyPageScenario.ORDER_TO_TABLE
                                    ) ?? true
                                }
                            />
                            <SettingDisplaySwitch
                                title="Dine In module shown on Web Journey Page"
                                description="If Dine In is not supported in location services, the module will not appear even when enabled"
                                checked={
                                    settings?.web?.journeyPageScenarios?.includes(
                                        JourneyPageScenario.DINE_IN
                                    ) ?? false
                                }
                            />
                            <SettingDisplaySwitch
                                title="Pay at Table module shown on Web Journey Page"
                                description="If Pay at Table is not supported in location services, the module will not appear even when enabled"
                                checked={
                                    settings?.web?.journeyPageScenarios?.includes(
                                        JourneyPageScenario.PAY_AT_TABLE
                                    ) ?? true
                                }
                            />
                        </>
                    )}
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="Show Recent Orders">
                                <SwitchFormField name="showRecentOrders" />
                            </SettingDisplay>
                            <SettingDisplay label="Show Parent Categories">
                                <SwitchFormField name="showCategoryGroups" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Card check enabled - Table"
                                description="Check the first user to join a table order has a stored card"
                            >
                                <SwitchFormField name="cardCheckEnabledTable" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Card check enabled - Tab"
                                description="Check the first user to join a tab order has a stored card"
                            >
                                <SwitchFormField name="cardCheckEnabledTab" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Card check enabled - Order to Table"
                                description="Check the first user to join an order to table order has a stored card"
                            >
                                <SwitchFormField name="cardCheckEnabledOrderToTable" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Card check enabled - Preorder"
                                description="Check the first user to join a preorder has a stored card"
                            >
                                <SwitchFormField name="cardCheckEnabledPreorder" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Card check enabled - Pay at POS"
                                description="Check the first user to join a pay at pos order has a stored card"
                            >
                                <SwitchFormField name="cardCheckEnabledPayAtPos" />
                            </SettingDisplay>
                            <SettingDisplay label="Calorie Nutrient Name">
                                <TextFormField name="calorieNutrientName" />
                            </SettingDisplay>
                            <SettingDisplay label="Buy Now Button on All Products">
                                <SwitchFormField name="buyNowEnabled" />
                            </SettingDisplay>
                            <SettingDisplay label="Use Product Details Image in Menu Listing">
                                <SwitchFormField name="useProductDetailsImageInMenuListing" />
                            </SettingDisplay>
                        </>
                    )}
                    {locationId !== undefined && (
                        <SettingDisplay label="Show Parent Categories">
                            <SwitchFormField name="showCategoryGroups" />
                        </SettingDisplay>
                    )}
                    <SettingDisplay label="Allergen Link">
                        <TextFormField name="allergenUrl" />
                    </SettingDisplay>
                    {locationId !== undefined && (
                        <>
                            <SettingDisplay
                                label="Ordering Enabled"
                                description="This setting controls Preorder, Dine In, Order to Table, Pay at Table and Delivery ordering"
                            >
                                <SwitchFormField name="orderingEnabled" />
                            </SettingDisplay>
                            <SettingDisplay label="Busyness">
                                <SelectFormField name="orderingBusyness" options={orderingBusynessOptions} />
                            </SettingDisplay>
                        </>
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplay
                                label="Menu Awards CTA Icon"
                                description="200px by 200px PNG or JPEG"
                            >
                                <ImageFormField
                                    name="menuAwardsIcon"
                                    aspect={1}
                                    fileName="menu_awards_cta_icon"
                                    uploadFolder="app_assets"
                                    accept="image/png, image/jpeg"
                                    unremovable={true}
                                />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Preorder module shown on Web Journey Page"
                                description="If Preorder is not supported in location services, the module will not appear even when enabled"
                            >
                                <SwitchFormField name="preorderEnabledJourney" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Order to Table module shown on Web Journey Page"
                                description="If Order to Table is not supported in location services,
                                    the module will not appear even when enabled"
                            >
                                <SwitchFormField name="ottEnabledJourney" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Dine In module shown on Web Journey Page"
                                description="If Dine In is not supported in location services, the module will not appear even when enabled"
                            >
                                <SwitchFormField name="dineInEnabledJourney" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Pay At Table module shown on Web Journey Page"
                                description="If Pay at Table is not supported in location services, the module will not appear even when enabled"
                            >
                                <SwitchFormField name="patEnabledJourney" />
                            </SettingDisplay>
                        </>
                    )}
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
